import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faStar, faSync, faTrash } from '@fortawesome/free-solid-svg-icons';

import accessLocalStorage from '../../../services/accessLocalStorage';
import { getCsatSurveys, deleteCsatSurvey } from '../../../services/csatApi';
import { hasFeature } from '../../../services/permissions';
import Loading from '../../Loading/LoadingModule';
import {
  CSAT_MESSAGES,
  csatDeleteQuestion,
  csatPermissions,
  resultErrorMessage,
  scaleLabel,
} from './csatScreen';

import '../../../pages/CallcenterV2/config-styles.css';
import '../IncomingCalls/IncomingCalls.css';

const readSession = () => accessLocalStorage.getUserLocalStorage();

const sessionTenant = () => {
  const session = readSession();
  return session && session.user ? session.user.idtenant : null;
};

function CsatSurveysList() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [warning, setWarning] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const { canCreate, canEdit, canDelete } = csatPermissions(hasFeature);

  const fetchSurveys = useCallback(async () => {
    const result = await getCsatSurveys(sessionTenant());
    if (result.success) {
      setSurveys(result.data);
      setLoadError(null);
    } else {
      setSurveys([]);
      setLoadError(resultErrorMessage(result, CSAT_MESSAGES.loadFailed));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!readSession()) {
      history.push('/');
      return;
    }
    fetchSurveys();
  }, [history, fetchSurveys]);

  const visibleSurveys = useMemo(() => {
    const term = search.trim().toLowerCase();
    return surveys.filter((survey) => {
      if (statusFilter === 'active' && !survey.active) return false;
      if (statusFilter === 'inactive' && survey.active) return false;
      if (!term) return true;
      return String(survey.name || '').toLowerCase().includes(term);
    });
  }, [surveys, search, statusFilter]);

  const activeCount = surveys.filter((survey) => survey.active).length;

  const closeDelete = () => {
    if (deleting) return;
    setSurveyToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!surveyToDelete || deleting) return;
    setDeleting(true);
    const result = await deleteCsatSurvey(surveyToDelete.id, sessionTenant());
    setDeleting(false);
    if (!result.success) {
      setDeleteError(resultErrorMessage(result, CSAT_MESSAGES.deleteFailed));
      return;
    }
    setSurveyToDelete(null);
    setNotice(CSAT_MESSAGES.deleted);
    setWarning(result.data && result.data.dialplanReloaded === false ? CSAT_MESSAGES.reloadPending : null);
    await fetchSurveys();
  };

  if (loading && surveys.length === 0) return <Loading />;

  return (
    <div className="incoming-page">
      <div className="incoming-content">
        <div className="incoming-header">
          <div className="incoming-title-wrapper">
            <FontAwesomeIcon icon={faStar} className="incoming-icon" />
            <div>
              <h1 className="incoming-title">{CSAT_MESSAGES.listTitle}</h1>
              <p className="incoming-subtitle">Cadastre as pesquisas tocadas depois do atendimento</p>
            </div>
          </div>
          <div className="incoming-actions">
            {canCreate && (
              <Link to="/config/csat/new" className="ccv2-btn ccv2-btn-primary" style={{ textDecoration: 'none' }}>
                <FontAwesomeIcon icon={faPlus} /> {CSAT_MESSAGES.newButton}
              </Link>
            )}
          </div>
        </div>

        <div className="ccv2-dashboard-kpis">
          <div className="ccv2-dashboard-kpi">
            <div className="ccv2-dashboard-kpi-value">{surveys.length}</div>
            <div className="ccv2-dashboard-kpi-label">Total de pesquisas</div>
          </div>
          <div className="ccv2-dashboard-kpi">
            <div className="ccv2-dashboard-kpi-value ccv2-kpi-green">{activeCount}</div>
            <div className="ccv2-dashboard-kpi-label">Pesquisas ativas</div>
          </div>
          <div className="ccv2-dashboard-kpi">
            <div className="ccv2-dashboard-kpi-value">{surveys.length - activeCount}</div>
            <div className="ccv2-dashboard-kpi-label">Pesquisas inativas</div>
          </div>
        </div>

        {notice && <div className="notification is-success is-light">{notice}</div>}
        {warning && <div className="notification is-warning is-light">{warning}</div>}
        {loadError && <div className="notification is-danger is-light">{loadError}</div>}

        <div className="ccv2-filters">
          <div className="ccv2-filter-group" style={{ flex: 2 }}>
            <label htmlFor="csat-search">Buscar</label>
            <input
              id="csat-search"
              type="text"
              placeholder="Nome da pesquisa"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="ccv2-filter-group">
            <label htmlFor="csat-status">Situação</label>
            <select id="csat-status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">Todas</option>
              <option value="active">Apenas ativas</option>
              <option value="inactive">Apenas inativas</option>
            </select>
          </div>
          <div className="ccv2-filter-actions">
            <button type="button" className="ccv2-btn ccv2-btn-sm ccv2-btn-secondary" onClick={() => { setNotice(null); setWarning(null); fetchSurveys(); }} title={CSAT_MESSAGES.refreshButton}>
              <FontAwesomeIcon icon={faSync} />
            </button>
          </div>
        </div>

        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>{CSAT_MESSAGES.nameLabel}</th>
                  <th>{CSAT_MESSAGES.scaleLabel}</th>
                  <th>{CSAT_MESSAGES.statusLabel}</th>
                  <th>{CSAT_MESSAGES.actionsLabel}</th>
                </tr>
              </thead>
              <tbody>
                {!loadError && visibleSurveys.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="ccv2-empty-row">{CSAT_MESSAGES.empty}</td>
                  </tr>
                ) : (
                  visibleSurveys.map((survey) => (
                    <tr key={survey.id}>
                      <td><span className="ccv2-cell-name">{survey.name}</span></td>
                      <td>{scaleLabel(survey.scale)}</td>
                      <td>
                        {survey.active ? (
                          <span className="ccv2-badge ccv2-badge--success">{CSAT_MESSAGES.active}</span>
                        ) : (
                          <span className="ccv2-badge ccv2-badge--danger">{CSAT_MESSAGES.inactive}</span>
                        )}
                      </td>
                      <td className="ccv2-cell-actions">
                        {canEdit && (
                          <Link to={`/config/csat/${survey.id}/edit`} className="ccv2-btn-icon ccv2-btn-edit" title={CSAT_MESSAGES.editTooltip} aria-label={CSAT_MESSAGES.editTooltip}>
                            <FontAwesomeIcon icon={faEdit} />
                          </Link>
                        )}
                        {canDelete && (
                          <button type="button" className="ccv2-btn-icon ccv2-btn-delete" title={CSAT_MESSAGES.deleteTooltip} aria-label={CSAT_MESSAGES.deleteTooltip} onClick={() => { setDeleteError(null); setSurveyToDelete(survey); }}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {surveyToDelete && (
        <div className="ccv2-modal-overlay" onClick={closeDelete}>
          <div className="ccv2-modal" onClick={(event) => event.stopPropagation()}>
            <div className="ccv2-modal-header" style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>
              <h2>
                <FontAwesomeIcon icon={faTrash} /> {CSAT_MESSAGES.deleteTitle}
              </h2>
              <button type="button" className="ccv2-modal-close" onClick={closeDelete}>&times;</button>
            </div>
            <div className="ccv2-modal-body">
              <p>{csatDeleteQuestion(surveyToDelete.name)}</p>
              <div className="incoming-warning-box">
                A pesquisa deixa de ser oferecida nas filas.
              </div>
              {deleteError && <p className="has-text-danger mt-2"><strong>{deleteError}</strong></p>}
            </div>
            <div className="ccv2-modal-footer">
              <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={closeDelete} disabled={deleting}>
                {CSAT_MESSAGES.cancelButton}
              </button>
              <button type="button" className="ccv2-btn" style={{ background: '#dc2626', color: '#fff' }} onClick={confirmDelete} disabled={deleting}>
                {deleting ? CSAT_MESSAGES.deleteLoading : CSAT_MESSAGES.deleteConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CsatSurveysList;
